/**
 * Twelve shelves, drawn at four different sizes.
 *
 * Size is information, not layout variety: the shelf that actually holds a
 * released game is the largest thing on the page, and the empty ones get less
 * room, a dashed edge and a plain "coming soon". A visitor can therefore read
 * the shape of the platform - one full shelf, eleven waiting - without
 * counting anything.
 */

import { CATEGORIES, categoryStatus, gamesInCategory } from "@kikik/game-catalog";
import { useI18n } from "../../i18n";
import { Motif } from "../art/Motif";

export interface WorldsSectionProps {
  onOpenGame: (slug: string) => void;
}

export function WorldsSection({ onOpenGame }: WorldsSectionProps) {
  const { t } = useI18n();

  return (
    <section
      className="section section--worlds"
      id="categories"
      aria-labelledby="worlds-title"
      data-reveal="out"
    >
      <div className="shell">
        <p className="eyebrow">{t("portal.worlds.eyebrow")}</p>
        <h2 className="section__title" id="worlds-title">
          {t("portal.worlds.title")}
        </h2>
        <p className="section__lead">{t("portal.worlds.lead")}</p>

        <ul className="worlds">
          {CATEGORIES.map((category) => {
            const open = categoryStatus(category.id) === "open";
            const games = gamesInCategory(category.id);
            return (
              <li
                key={category.id}
                className="world"
                data-size={category.size}
                data-accent={category.accent}
                data-open={open}
                data-category={category.id}
              >
                <Motif motif={category.motif} className="world__art" />
                <div className="world__text">
                  <h3 className="world__name">{t(category.nameKey)}</h3>
                  <p className="world__blurb">{t(category.blurbKey)}</p>
                </div>

                {open ? (
                  <div className="world__games">
                    {games.map((game) => (
                      <button
                        key={game.slug}
                        type="button"
                        className="world__game"
                        onClick={() => onOpenGame(game.slug)}
                      >
                        <span className="world__game-name">{t(game.titleKey)}</span>
                        <span className="world__game-go" aria-hidden="true">
                          →
                        </span>
                      </button>
                    ))}
                  </div>
                ) : (
                  <p className="world__soon">
                    <span className="world__soon-mark" aria-hidden="true" />
                    {t("portal.status.comingSoon")}
                  </p>
                )}
              </li>
            );
          })}
        </ul>
      </div>
    </section>
  );
}
